import type { AccessPolicy as AccessPolicyShape } from '../types/auth';

export type ShellPreference = 'mobile' | 'desktop';

const SHELL_PREFERENCE_KEY = 'coop_shell_preference';
export const MERCHANT_MOBILE_SWITCH_PATH = '/merchant/mobile';

export class AccessPolicy {
  private readonly policy: AccessPolicyShape | null;

  constructor(policy: AccessPolicyShape | null | undefined) {
    this.policy = policy ?? null;
  }

  can(action: keyof AccessPolicyShape['capabilities']) {
    if (!this.policy) return false;
    return Boolean(this.policy.capabilities[action]);
  }

  isReadOnly(section: 'base' | 'tips' | string) {
    if (!this.policy) return false;
    if (section === 'tips') return !this.policy.capabilities.editTipsSplits;
    if (section === 'base') return !this.policy.capabilities.editBaseSplits;
    return false;
  }

  visibleNavItems<T extends { label: string }>(items: T[]): T[] {
    if (!this.policy || this.policy.workspace === 'admin') return items;
    const hiddenLabels = new Set(['Permissions', 'Tax policies', 'Users']);
    return items.filter((item) => !hiddenLabels.has(item.label));
  }

  defaultMerchantId() {
    return this.policy?.defaultFilters.merchantId ?? null;
  }

  defaultEventId() {
    return this.policy?.defaultFilters.eventId ?? null;
  }

  get workspace() {
    return this.policy?.workspace ?? 'admin';
  }

  get raw() {
    return this.policy;
  }
}

export function isMobileViewport() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

export function readShellPreference(): ShellPreference | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(SHELL_PREFERENCE_KEY);
    if (raw === 'mobile' || raw === 'desktop') return raw;
    return null;
  } catch {
    return null;
  }
}

export function saveShellPreference(preference: ShellPreference) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(SHELL_PREFERENCE_KEY, preference);
  } catch {
    // ignore
  }
}

export function clearShellPreference() {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(SHELL_PREFERENCE_KEY);
  } catch {
    // ignore
  }
}

export function canUseMerchantPortal(policy: AccessPolicyShape | null | undefined) {
  if (!policy) return false;
  return policy.scope.allMerchants || policy.scope.merchantIds.length > 0;
}

export function canUseAdminWorkspace(policy: AccessPolicyShape | null | undefined) {
  return Boolean(policy?.user?.roles?.includes('platform_admin'));
}

export function shouldShowMerchantMobileReturn(
  policy: AccessPolicyShape | null | undefined,
  pathname: string,
  preferredShell: ShellPreference | null = readShellPreference(),
) {
  if (!canUseMerchantPortal(policy)) return false;
  if (pathname === MERCHANT_MOBILE_SWITCH_PATH || pathname.startsWith('/merchant/portal')) return false;
  if (pathname.startsWith('/merchant')) return true;
  return pathname.startsWith('/admin') && preferredShell === 'desktop';
}

export function resolveScopedMerchantLandingPath(
  policy: AccessPolicyShape | null | undefined,
  options?: {
    fromPath?: string | null;
    preferredShell?: ShellPreference | null;
    mobileHint?: boolean;
  },
): '/admin' | '/merchant/portal' | '/merchant/desktop' | string {
  const preferredShell = options?.preferredShell ?? readShellPreference();

  if (preferredShell === 'mobile') {
    return resolveMerchantMobilePath(policy);
  }
  if (preferredShell === 'desktop') {
    return canUseMerchantPortal(policy) ? '/merchant/desktop' : resolveMerchantMobilePath(policy);
  }

  if (options?.mobileHint ?? isMobileViewport()) {
    return resolveMerchantMobilePath(policy);
  }

  if (options?.fromPath?.startsWith('/merchant')) {
    return options.fromPath;
  }

  return canUseMerchantPortal(policy) ? '/merchant/desktop' : resolveMerchantMobilePath(policy);
}

export function resolveMerchantDesktopPath(policy: AccessPolicyShape | null | undefined): '/admin' | '/merchant/desktop' {
  if (!policy) return '/merchant/desktop';
  if (canUseAdminWorkspace(policy)) return '/admin';
  return canUseMerchantPortal(policy) ? '/merchant/desktop' : '/admin';
}

export function resolveMerchantMobilePath(policy: AccessPolicyShape | null | undefined): '/admin' | '/merchant/portal' {
  if (canUseMerchantPortal(policy)) return '/merchant/portal';
  return '/admin';
}

export function resolveMerchantLandingPath(
  policy: AccessPolicyShape | null | undefined,
  options?: {
    fromPath?: string | null;
    preferredShell?: ShellPreference | null;
    mobileHint?: boolean;
  },
): '/admin' | '/merchant/portal' | '/merchant/desktop' | string {
  const preferredShell = options?.preferredShell ?? readShellPreference();

  if (preferredShell === 'mobile') {
    return resolveMerchantMobilePath(policy);
  }
  if (preferredShell === 'desktop') {
    return resolveMerchantDesktopPath(policy);
  }

  if (options?.mobileHint ?? isMobileViewport()) {
    return resolveMerchantMobilePath(policy);
  }

  if (options?.fromPath) {
    const fromPath = options.fromPath;
    if (policy?.workspace === 'admin') return fromPath;
    if (fromPath.startsWith('/merchant')) return fromPath;
  }

  return resolveMerchantDesktopPath(policy);
}

export const workspaceHomePath = (
  workspace: AccessPolicyShape['workspace'] | null | undefined,
): '/admin' | '/merchant/portal' => {
  if (workspace === 'admin') return '/admin';
  return '/merchant/portal';
};

export const impersonationLandingPath = (
  policy: AccessPolicyShape | null | undefined,
): '/admin' | '/merchant/portal' | '/merchant/desktop' | string => {
  if (!policy) return resolveMerchantLandingPath(null);
  if (canUseAdminWorkspace(policy)) return '/admin';
  if (canUseMerchantPortal(policy)) return resolveScopedMerchantLandingPath(policy);
  return resolveMerchantLandingPath(policy);
};
